import config from '../service/config';
import logging from '../service/logging';
import {ITeamModel, ITeamModelFolderTree} from '../model/appModel';
import teamApi from '../service/teamApi';
import folderApi from '../service/folderApi';
import invitationApi, {IInvitationModel} from '../service/invitationApi';
import {create} from 'zustand';
import {ITeamQuotasResponse} from '../model/userQuotas';
import Constants, {GroupRoleTypes} from '../constants/Constants';
import {History} from 'history';
import {IFolderTreeNode, addFolderToFolderTree, replaceFolderInFolderTree, removeFolderFromFolderTree, findFolderByHash} from '../model/folderTreeNode';

interface ITeamProvider {
   userTeamsFolderTrees: ITeamModelFolderTree[];
   initialUserTeamsLoadingCompleted: boolean;
   activeTeam: ITeamModel;
   activeTeamFolderTree: ITeamModelFolderTree;
   isUpdatingUserTeams: boolean;
   canLoadMoreTeams: boolean;
   isUpdatingInvitations: boolean;
   teamInvitations: IInvitationModel;
   teamQuotas: ITeamQuotasResponse;
   history: History;
}

interface ISet {
   updateUserTeamsWithFolderTrees: () => Promise<void>;
   updateFoldersInUserTeamsFolderTrees: (teamWithFolderTree: ITeamModelFolderTree, updatedFolders: IFolderTreeNode[]) => void;
   updateFolderLocationsInUserTeamsFolderTrees: (teamWithFolderTree: ITeamModelFolderTree, foldersToMove: IFolderTreeNode[], targetFolderLocationHash?: string) => void;
   addFolderToUserTeamsFolderTrees: (teamWithFolderTree: ITeamModelFolderTree, updatedFolder: IFolderTreeNode) => void;
   removeFoldersFromUserTeamsFolderTrees: (teamWithFolderTree: ITeamModelFolderTree, updatedFolder: IFolderTreeNode[]) => void;
   updateTeamInvitationList: () => Promise<void>;
   renameTeam: (teamId: string, name: string) => void;
   createTeam: (team: ITeamModel) => Promise<void>;
   removeTeam: (teamId: string) => void;
   updateActiveTeam: (team: ITeamModel) => Promise<void>;
   updateActiveTeamWithId: (teamId: string) => Promise<void>;
   updateActiveTeamAndRedirectToTeam: (team: ITeamModel) => Promise<void>;
   updateActiveTeamWithIdAndRedirectToTeam: (teamId: string) => Promise<void>;
   clearActiveTeam: () => void;
   clearActiveTeamAndNavigateToTeamsPage: () => void;
   updateTeamQuotas: (teamId: string) => Promise<void>;
   setHistory: (history: History) => void;
}

const initialState: ITeamProvider = {
   userTeamsFolderTrees: [],
   initialUserTeamsLoadingCompleted: false,
   activeTeam: null,
   activeTeamFolderTree: null,
   isUpdatingUserTeams: false,
   canLoadMoreTeams: true,
   isUpdatingInvitations: false,
   teamInvitations: null,
   teamQuotas: null,
   history: null
};

const getTeams = async () => {
   try {
      return await teamApi.getTeamList();
   } catch (e) {
      logging.error('Failed to update collections data from server: ', e);
      return [];
   }
};

const getFolderTree = async (team: ITeamModel): Promise<ITeamModelFolderTree> => {
   try {
      const folderTree = await folderApi.getFolderTree(null, team.TeamId);
      return {...team, Foldertree: folderTree} as ITeamModelFolderTree;
   } catch (e) {
      logging.error('Failed to update collections data from server: ', e);
      return null;
   }
};

const getQuotas = async (teamId: string): Promise<ITeamQuotasResponse> => {
   try {
      return await teamApi.getUserTeamQuotas(teamId);
   } catch (e) {
      logging.error('Failed to update quotas data from server: ', e);
      return null;
   }
};

const useTeamsStore = create<ITeamProvider & ISet>((set, get) => ({
   ...initialState,
   updateUserTeamsWithFolderTrees: async () => {
      if (!config.featureSwitches.EnableTeams || get().isUpdatingUserTeams) {
         return;
      }
      set({isUpdatingUserTeams: true});
      const teams = await getTeams();
      const teamFolderTrees: ITeamModelFolderTree[] = [];
      await Promise.all(teams.map(team => getFolderTree(team).then(folderTree => teamFolderTrees.push(folderTree))));

      const sortedTeamFolderTrees = teamFolderTrees.sort((a, b) => a.Name.localeCompare(b.Name));

      set({userTeamsFolderTrees: sortedTeamFolderTrees, isUpdatingUserTeams: false, initialUserTeamsLoadingCompleted: true});
   },
   updateFoldersInUserTeamsFolderTrees: (teamWithFolderTree: ITeamModelFolderTree, updatedFolders: IFolderTreeNode[]) => {
      set(prevState => {
         let updatedUserTeamsFolderTrees = prevState.userTeamsFolderTrees;
         let updatedActiveTeamFolderTree = prevState.activeTeamFolderTree;

         updatedFolders.forEach(updatedFolder => {
            updatedUserTeamsFolderTrees = updatedUserTeamsFolderTrees.map(userTeam =>
               userTeam.TeamId === teamWithFolderTree.TeamId
                  ? {...userTeam, Foldertree: replaceFolderInFolderTree(userTeam.Foldertree, updatedFolder)}
                  : userTeam
            );

            if (updatedActiveTeamFolderTree?.TeamId === teamWithFolderTree.TeamId) {
               updatedActiveTeamFolderTree = {...updatedActiveTeamFolderTree, Foldertree: replaceFolderInFolderTree(updatedActiveTeamFolderTree.Foldertree, updatedFolder)};
            }
         });

         return {userTeamsFolderTrees: updatedUserTeamsFolderTrees, activeTeamFolderTree: updatedActiveTeamFolderTree};
      });
   },
   updateFolderLocationsInUserTeamsFolderTrees: (teamWithFolderTree: ITeamModelFolderTree, foldersToMove: IFolderTreeNode[], targetFolderLocationHash?: string) => {
      set(prevState => {
         let newFolderTree = teamWithFolderTree.Foldertree;
         foldersToMove.forEach(folder => {
            newFolderTree = removeFolderFromFolderTree(newFolderTree, folder.Hash);
         });
         const targetFolderLocation = targetFolderLocationHash || targetFolderLocationHash !== Constants.rootFolderHash ? findFolderByHash(teamWithFolderTree.Foldertree, targetFolderLocationHash) : null;

         foldersToMove.forEach(folder => {
            const updatedFolderWithNewParentFolder = {...folder, ParentFolder: targetFolderLocation ? {...targetFolderLocation} : null} as IFolderTreeNode;

            if (targetFolderLocation) {
               newFolderTree = addFolderToFolderTree(newFolderTree, targetFolderLocation.Hash, updatedFolderWithNewParentFolder);
            } else {
               newFolderTree = {...newFolderTree, SubFolders: [...newFolderTree.SubFolders, updatedFolderWithNewParentFolder]};
            }
         });

         const updatedUserTeamsFolderTrees = prevState.userTeamsFolderTrees.map(userTeam =>
            userTeam.TeamId === teamWithFolderTree.TeamId
               ? {...userTeam, Foldertree: newFolderTree}
               : userTeam
         );

         let updatedActiveTeamFolderTree = prevState.activeTeamFolderTree;
         if (prevState.activeTeamFolderTree?.TeamId === teamWithFolderTree.TeamId) {
            updatedActiveTeamFolderTree = {...prevState.activeTeamFolderTree, Foldertree: newFolderTree};
         }
         return {userTeamsFolderTrees: updatedUserTeamsFolderTrees, activeTeamFolderTree: updatedActiveTeamFolderTree};
      });
   },
   addFolderToUserTeamsFolderTrees: (teamWithFolderTree: ITeamModelFolderTree, newFolder: IFolderTreeNode) => {
      const parentHash = newFolder.ParentFolder?.Hash;

      set(prevState => {
         const updatedUserTeamsFolderTrees = prevState.userTeamsFolderTrees.map(userTeam =>
            userTeam.TeamId === teamWithFolderTree.TeamId
               ? {...userTeam, Foldertree: parentHash ? addFolderToFolderTree(userTeam.Foldertree, parentHash, newFolder) : {...userTeam.Foldertree, SubFolders: [...userTeam.Foldertree.SubFolders, newFolder]}}
               : userTeam
         );

         let updatedActiveTeamFolderTree = prevState.activeTeamFolderTree;
         if (prevState.activeTeamFolderTree && prevState.activeTeamFolderTree.TeamId === teamWithFolderTree.TeamId) {
            updatedActiveTeamFolderTree = {
               ...prevState.activeTeamFolderTree,
               Foldertree: parentHash
                  ? addFolderToFolderTree(prevState.activeTeamFolderTree.Foldertree, parentHash, newFolder)
                  : {...prevState.activeTeamFolderTree.Foldertree, SubFolders: [...prevState.activeTeamFolderTree.Foldertree.SubFolders, newFolder]}
            };
         }

         return {userTeamsFolderTrees: updatedUserTeamsFolderTrees, activeTeamFolderTree: updatedActiveTeamFolderTree};
      });
   },
   removeFoldersFromUserTeamsFolderTrees: (teamWithFolderTree: ITeamModelFolderTree, foldersToRemove: IFolderTreeNode[]) => {
      set(prevState => {
         let updatedUserTeamsFolderTrees = prevState.userTeamsFolderTrees;
         let updatedActiveTeamFolderTree = prevState.activeTeamFolderTree;

         foldersToRemove.forEach(folderToRemove => {
            updatedUserTeamsFolderTrees = updatedUserTeamsFolderTrees.map(userTeam =>
               userTeam.TeamId === teamWithFolderTree.TeamId
                  ? {...userTeam, Foldertree: removeFolderFromFolderTree(userTeam.Foldertree, folderToRemove.Hash)}
                  : userTeam
            );

            if (updatedActiveTeamFolderTree?.TeamId === teamWithFolderTree.TeamId) {
               updatedActiveTeamFolderTree = {...updatedActiveTeamFolderTree, Foldertree: removeFolderFromFolderTree(updatedActiveTeamFolderTree.Foldertree, folderToRemove.Hash)};
            }
         });

         return {userTeamsFolderTrees: updatedUserTeamsFolderTrees, activeTeamFolderTree: updatedActiveTeamFolderTree};
      });
   },
   updateTeamInvitationList: async () => {
      if (!config.featureSwitches.EnableTeams || get().isUpdatingInvitations) {
         return;
      }

      set({isUpdatingInvitations: true});
      const invitations = await invitationApi.getInvitations(true);

      if (invitations) {
         set({teamInvitations: invitations});
      }
      set({isUpdatingInvitations: false});
   },
   renameTeam: (teamId: string, name: string) => {
      if (!config.featureSwitches.EnableTeams) {
         return;
      }
      set(state => {
         const teamFolderIndex = state.userTeamsFolderTrees?.findIndex(c => c.TeamId === teamId);

         if (teamFolderIndex === -1) {
            return {};
         }
         const updatedTeam = state.userTeamsFolderTrees ? {...state.userTeamsFolderTrees[teamFolderIndex], Name: name} : null;
         const updatedTeams = state.userTeamsFolderTrees?.map(team =>
            team.TeamId === teamId ? updatedTeam : team
         );
         const newState = {...state, userTeamsFolderTrees: updatedTeams};

         if (state.activeTeam?.TeamId === teamId) {
            newState.activeTeam = updatedTeam;
         }

         return newState;
      });
   },
   createTeam: async (team: ITeamModel) => {
      if (!config.featureSwitches.EnableTeams) {
         return;
      }
      const folderTree = await getFolderTree(team);
      set(state => ({
         userTeamsFolderTrees: [
            ...(state.userTeamsFolderTrees ?? []),
            folderTree
         ]
      }));
   },
   removeTeam: (teamId: string) => {
      if (!config.featureSwitches.EnableTeams) {
         return;
      }
      set(state => ({userTeamsFolderTrees: state.userTeamsFolderTrees?.filter(t => t.TeamId !== teamId)}));
   },
   updateActiveTeam: async (team: ITeamModel) => {
      if (!config.featureSwitches.EnableTeams) {
         return;
      }
      const teamWithFolderTree = await getFolderTree(team);

      let userTeams = get().userTeamsFolderTrees as ITeamModel[];
      if (!userTeams || userTeams?.length === 0) {
         userTeams = await getTeams();
         const teamFolderTrees: ITeamModelFolderTree[] = [];
         await Promise.all(userTeams.map(userTeam => getFolderTree(userTeam).then(folderTree => teamFolderTrees.push(folderTree))));

         const sortedTeamFolderTrees = teamFolderTrees.sort((a, b) => a.Name.localeCompare(b.Name));
         set({userTeamsFolderTrees: sortedTeamFolderTrees});
      }
      teamWithFolderTree.LoggedInUserHasCreatePermission = userTeams?.some(t => t.TeamId === teamWithFolderTree.TeamId && (t.Role === GroupRoleTypes.contributor || t.Role === GroupRoleTypes.manager));
      teamWithFolderTree.LoggedInUserIsTeamOwner = userTeams?.some(t => t.TeamId === teamWithFolderTree.TeamId && t.Role === GroupRoleTypes.manager);

      set({activeTeam: teamWithFolderTree as ITeamModel, activeTeamFolderTree: teamWithFolderTree});
   },
   updateActiveTeamWithId: async (teamId: string) => {
      const team = await teamApi.getTeam(teamId);
      const updateActiveTeam = get().updateActiveTeam;
      await updateActiveTeam(team);
   },
   updateActiveTeamAndRedirectToTeam: async (team: ITeamModel) => {
      const updateActiveTeam = get().updateActiveTeam;
      await updateActiveTeam(team);

      set(prevState => {
         prevState.history.push(`${Constants.navigation.teamsLink}/${team.TeamId}`);
         return {};
      });
   },
   updateActiveTeamWithIdAndRedirectToTeam: async (teamId: string) => {
      const team = await teamApi.getTeam(teamId);
      const updateActiveTeamAndRedirectToTeam = get().updateActiveTeamAndRedirectToTeam;
      await updateActiveTeamAndRedirectToTeam(team);
   },
   clearActiveTeam: () => {
      set({activeTeam: null, activeTeamFolderTree: null});
   },
   clearActiveTeamAndNavigateToTeamsPage: () => {
      set(prevState => {
         prevState.history.push(`${Constants.navigation.teamsLink}`);
         return {
            activeTeam: null,
            activeTeamFolderTree: null
         };
      });
   },
   updateTeamQuotas: async (teamId: string) => {
      if (!config.featureSwitches.EnableTeams) {
         return;
      }
      const teamQuotas = await getQuotas(teamId);
      teamQuotas.UserHasQuotaInformation = true;
      set({teamQuotas: teamQuotas});
   },
   setHistory: (historySetting: History) => {
      set({history: historySetting});
   }
}));

export {
   useTeamsStore
};